import { createFileRoute } from "@tanstack/react-router";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Pal Stars" },
      { name: "description", content: "Terms of use for the Pal Stars Palworld fan wiki: acceptable use, IP notices, disclaimers and liability." },
      { property: "og:title", content: "Terms of Service — Pal Stars" },
      { property: "og:description", content: "Acceptable use, IP notices, disclaimers and liability for Pal Stars." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/terms" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/terms" }],
    scripts: [breadcrumbSchema([["Home", "/"], ["Terms of Service", "/terms"]]), webPageSchema({ name: "Terms of Service", description: "Acceptable use, IP notices, disclaimers and liability for the Pal Stars fan wiki.", path: "/terms" })],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-12 prose prose-invert">
      <h1 className="text-3xl sm:text-4xl font-bold">Terms of Service</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: July 27, 2026</p>

      <p className="mt-6">
        By accessing palstars.com you agree to these terms. If you do not agree, please do not use the site.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">1. Fan project, not affiliated</h2>
      <p className="mt-2">
        Pal Stars is an unofficial fan-made database and wiki. It is not affiliated with, endorsed by, sponsored
        by, or approved by Pocketpair Inc. or the developers of Palworld. All trademarks, logos, game names and
        character designs are the property of their respective owners.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">2. Acceptable use</h2>
      <ul className="mt-2 list-disc pl-6 space-y-1">
        <li>Do not scrape, crawl or copy the site at abusive rates.</li>
        <li>Do not attempt to disrupt the site, other users, or attached services.</li>
        <li>Do not submit unlawful, defamatory or infringing content through the contact form.</li>
      </ul>

      <h2 className="mt-8 text-xl font-semibold text-foreground">3. Content accuracy</h2>
      <p className="mt-2">
        Pal names, stats, breeding data and tier lists are compiled from community sources and may be incomplete
        or out of date. Nothing on this site is official game guidance.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">4. External links</h2>
      <p className="mt-2">
        We link to third-party sites (news outlets, official Palworld resources, community wikis). We are not
        responsible for the content, privacy practices or availability of those sites.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">5. Intellectual property</h2>
      <p className="mt-2">
        Pal Stars' original layout, code, procedural artwork and written guides belong to their authors. If you
        believe content on the site infringes your rights, contact us via the contact page and we will review.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">6. Disclaimer & liability</h2>
      <p className="mt-2">
        The site is provided "as is" without warranties of any kind. To the maximum extent permitted by law, we
        are not liable for any damages arising from use of the site.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">7. Affiliate disclosure</h2>
      <p className="mt-2">
        When you click on links to various merchants on this site and make a purchase, this can result in this site
        earning a commission. Affiliate programs and affiliations include, but are not limited to, the eBay Partner
        Network. Every price page prints the exact date and time its prices were last updated.
      </p>
      <p className="mt-2">
        Commissions never change the price you pay and never change how a card, sealed product or item of merchandise is
        ranked, priced or described on this site. Prices, availability and listings on third-party marketplaces change
        constantly and may differ from the figures shown here; always confirm the final price with the seller before
        buying. Pal Stars is not a party to any transaction between you and a marketplace seller and is not responsible
        for orders, payments, shipping, returns or disputes.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">8. Prices &amp; market data</h2>
      <p className="mt-2">
        Price charts, indices, expected-value calculators and sold data are informational estimates compiled from public
        marketplace listings. They are not financial, investment or trading advice, and no outcome is guaranteed. You are
        solely responsible for any buying, selling or collecting decisions you make.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">9. Virtual items &amp; arcade games</h2>
      <p className="mt-2">
        Virtual packs, spins, gold and badges have no cash value, cannot be purchased, sold or exchanged for money, and
        may be adjusted or reset at any time. The arcade and Palsino features are cosmetic entertainment only and are not
        gambling.
      </p>

      <h2 className="mt-8 text-xl font-semibold text-foreground">10. Changes</h2>
      <p className="mt-2">We may update these terms. Continued use of the site after changes means you accept the updated terms.</p>
    </article>
  );
}
